import {Box, Button, Flex, Stack, Text, useDisclosure, VStack} from '@chakra-ui/react';
import MinusSvg from 'assets/SVG/MinusSvg';
import PlusSvg from 'assets/SVG/PlusSvg';
import DiscountLabel from 'components/metro/components/DiscountLabel';
import {AlertClearCart} from 'components/pages/restaurant';
import {colors, useCartContext} from 'components/providers';
import {Price} from 'components/shared';
import {FC, useMemo} from 'react';
import {useCartStore} from 'store/cartStore';
import {ICartItem, IProduct, IRestaurantResponse} from 'types';
import NextImageUI from 'ui/NextImage';
import {v4 as uuidv4} from 'uuid';

import {Labels} from './components';

interface Props {
  product: IProduct;
  onClick: (product: IProduct) => void;
  is_selected?: boolean;
  locale?: string;
  provider?: IRestaurantResponse;
  disablePlus?: boolean;
  isFromSlider?: boolean;
}

export const ProviderProduct: FC<Props> = ({onClick, product, is_selected, provider, disablePlus, isFromSlider}) => {
  const {isOpen: isOpenAlertClearCart, onOpen: onOpenAlertClearCart, onClose: onCloseAlertClearCart} = useDisclosure();

  const {cartItems, addProductToCart, changeCartItemQuantity, deleteProduct, addProvider, cartProvider, clearCart} =
    useCartContext();
  const {setCartItems} = useCartStore();
  const {name, price, discount, discount_price, ingredients, measurement_display, product_img_for_list_thumbnail} =
    product;

  const cartProduct = useMemo(() => cartItems.find((item) => item.productId === product.id), [product.id, cartItems]);

  const handleAddProduct = async () => {
    if (product?.option_sets?.length > 0 || !provider) return onClick(product);
    if (cartProduct) return changeCartItemQuantity(cartProduct.id, cartProduct.quantity + 1);

    let productTracking = 'restaurant/';
    // Tracking product origin - added from carousel, crosseling, recommended
    const isFromReccomended = provider?.recommended_products.find((item) => item.id === product.id);
    productTracking += isFromReccomended ? 'restaurant_recommended' : 'restaurant';

    const newCartItem: ICartItem = {
      id: uuidv4(),
      product: product,
      productId: product.id,
      price: product.price,
      quantity: 1,
      instructions: '',
      selectedOptions: {},
      added_from: productTracking,
    };
    if (cartProvider?.slug && provider?.slug !== cartProvider?.slug) {
      return onOpenAlertClearCart();
    } else {
      addProductToCart(newCartItem);
    }

    if (provider) addProvider(provider);
  };

  const handleDelete = () => {
    if (cartProduct?.quantity === 1) {
      deleteProduct(cartProduct.id);
      return;
    } else {
      if (!cartProduct?.id) return;
      changeCartItemQuantity(cartProduct.id, cartProduct.quantity - 1);
    }
  };

  const handleClick = () => {
    onClick(product);
  };

  return (
    <>
      <VStack
        alignItems="stretch"
        cursor="pointer"
        borderRadius="16px"
        p={['12px', '15px']}
        h="full"
        position="relative"
        borderLeft={isFromSlider ? '0px' : '4px solid'}
        bg={colors.white}
        borderLeftColor={is_selected ? colors.pink : 'transparent'}
        onClick={() => handleClick()}
      >
        <Box position="relative" mb={['0px', '15px']}>
          {is_selected && (
            <Flex
              position="absolute"
              w="100%"
              h="100%"
              alignItems="center"
              justify="center"
              bg="rgba(0, 0, 0, 0.5)"
              borderRadius="16px"
              zIndex={1}
            >
              <Text color="white" fontWeight={600} fontSize={['24px']}>
                {cartProduct?.quantity}
              </Text>
            </Flex>
          )}
          <Box width="100%" height={['140px', '214px']} position="relative" overflow="hidden" borderRadius="16px">
            <NextImageUI
              src={product_img_for_list_thumbnail}
              alt={`Product ${name}`}
              fallbacksrc="/food_pattern.webp"
              fill
              sizes="(max-width: 250px) 180px, 200px"
              style={{
                borderRadius: '16px',
                objectFit: 'cover',
              }}
            />
          </Box>
        </Box>
        <Labels product={product} />
        {discount && discount_price ? (
          <Box position="absolute" top="35px" left="0px">
            <DiscountLabel oldPrice={product.price} price={product.discount_price} />
          </Box>
        ) : null}

        <VStack flex={1} alignItems="stretch" justifyContent="space-between" spacing={0}>
          <Text
            fontSize={['12px', '16px']}
            fontWeight="700"
            pb="12px"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {name}
          </Text>
          <Box w="full" mb="12px !important" minH="42px">
            {measurement_display ? (
              <Text noOfLines={2} as="span" fontSize={['12px', '14px']} fontWeight="500" color={colors.textGray}>
                {ingredients}
                <Text fontSize={['12px', '14px']} fontWeight="400">
                  {measurement_display}
                </Text>
              </Text>
            ) : (
              <Text noOfLines={2} as="span" fontSize={['12px', '14px']} fontWeight="500" color={colors.textGray}>
                {ingredients}
              </Text>
            )}
          </Box>
          <Button
            as={isFromSlider ? 'div' : 'button'}
            bg={is_selected ? colors.pink : colors.bodyBackground}
            variant="gray"
            _focus={{border: 'none'}}
            fontSize="18px"
            width="100%"
            height="46px"
            alignSelf="flex-start"
            px={0}
            overflow="hidden"
          >
            <Flex w="full" justify="space-around" alignItems="center" textColor={is_selected ? colors.white : ''}>
              {is_selected ? (
                <Flex
                  h="46px"
                  px={['10px', '20px']}
                  alignItems="center"
                  justifyItems="center"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                >
                  <Text fontWeight="bold">
                    <MinusSvg fill={is_selected ? colors.white : ''} />
                  </Text>
                </Flex>
              ) : (
                <Flex h="46px" px={['10px', '20px']} />
              )}

              <Flex
                w="full"
                justify="space-around"
                alignItems="center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddProduct();
                }}
                h="46px"
              >
                {discount && discount_price && !is_selected ? (
                  <Flex alignItems="center" justifyContent="center">
                    <Stack
                      direction={['column', 'row']}
                      spacing={['4px', '10px']}
                      alignItems="center"
                      justifyContent="center"
                      textAlign="center"
                    >
                      <Price
                        value={discount_price}
                        priceProps={{
                          fontWeight: '600',
                          color: colors.pink,
                          fontSize: ['12px', '16px'],
                        }}
                      />
                      <Price
                        value={price}
                        priceProps={{
                          fontWeight: '600',
                          textDecorationLine: 'line-through',
                          fontSize: ['12px', '16px'],
                        }}
                      />
                    </Stack>
                  </Flex>
                ) : (
                  <Price
                    priceProps={{fontWeight: '600', textAlign: 'center', fontSize: ['12px', '16px']}}
                    value={discount ? discount_price : price}
                    showTextPrice={price < 0.1}
                  />
                )}
              </Flex>

              <Flex
                h="46px"
                px={['10px', '20px']}
                alignItems="center"
                justifyItems="center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddProduct();
                }}
              >
                <Text fontWeight="bold">{!disablePlus && <PlusSvg fill={is_selected ? colors.white : ''} />}</Text>
              </Flex>
            </Flex>
          </Button>
        </VStack>
      </VStack>
      {isOpenAlertClearCart && (
        <AlertClearCart
          isOpen={isOpenAlertClearCart}
          onClose={onCloseAlertClearCart}
          onConfirm={() => {
            clearCart(true);
            let productTracking = 'restaurant/';
            // Tracking product origin - added from carousel, crosseling, recommended
            const isFromReccomended = provider?.recommended_products.find((item) => item.id === product.id);
            productTracking += isFromReccomended ? 'restaurant_recommended' : 'restaurant';

            const newCartItem: ICartItem = {
              id: uuidv4(),
              product: product,
              productId: product.id,
              price: product.price,
              quantity: 1,
              instructions: '',
              selectedOptions: {},
              added_from: productTracking,
            };
            setCartItems([newCartItem]);
            if (provider) addProvider(provider);
          }}
          providerName={cartProvider?.name}
        />
      )}
    </>
  );
};
